import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useGameStore } from '../../store/game-store';
import type { Move, GameVariant, DrawnOpeningInfo } from '@draughts/shared';
import { formatMoveNotation } from '@draughts/core';
import { pairMoves } from './move-pairs';
import { useBlindGame } from './blind-context';
import { titoloApertura } from '../../utils/opening-labels';
import './MoveList.css';

// Stable empty array to avoid new reference on every selector call
const EMPTY_MOVES: Move[] = [];

export interface MoveListProps {
  /**
   * Moves to list. Omit for the player's own game, which reads them from the
   * multiplayer store; pass them when the board being shown belongs to a
   * different store — a spectated game, say — or the list would render the
   * viewer's own (empty) game instead.
   */
  moves?: Move[];
  /** Variant the notation is formatted for. Defaults to the multiplayer store's. */
  variant?: GameVariant;
  /**
   * L'apertura sorteggiata della partita, per l'intestazione «APERTURA N° 56».
   * `null`/assente con la mossa libera: niente intestazione.
   */
  opening?: DrawnOpeningInfo | null;
}

function MoveList({ moves, variant: variantProp, opening }: MoveListProps) {
  const { t } = useTranslation();
  const storeMoves = useMultiplayerStore((state) => state.boardState?.moveHistory ?? EMPTY_MOVES);
  /*
   * Il ripiego legge la VARIANTE DELLA DAMIERA, non il campo di store: le mosse
   * e la notazione con cui si scrivono devono venire dalla stessa partita (la
   * riga sopra prende da li' anche `moveHistory`). Il campo di store e' quel che
   * ha lasciato la partita PRECEDENTE.
   *
   * Qui non e' un'etichetta, e' il FORMATO: russa e brasiliana si scrivono in
   * notazione algebrica (`c3xg7`), tutte le altre coi numeri di casella — e la
   * numerica tiene il percorso intero della presa (`28x17x6`), l'algebrica solo
   * gli estremi. Con la variante sbagliata una partita russa si leggeva a
   * numeri. (Fra due varianti NUMERICHE la stringa coincide: la casella la
   * stampa la mossa, non la variante.)
   *
   * L'unico chiamante senza prop e' `OnlineGame`; torneo, arena e spettatore
   * passano `notationVariant` e non cambiano comportamento.
   */
  const varianteDellaDamiera = useMultiplayerStore((state) => state.boardState?.variant);
  const moveHistory = moves ?? storeMoves;
  const variant = variantProp ?? varianteDellaDamiera;
  const reviewIndex = useGameStore((state) => state.reviewIndex);
  const reviewToIndex = useGameStore((state) => state.reviewToIndex);
  const blind = useBlindGame();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new moves
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [moveHistory.length]);

  /* Numero di tabella e forza, dall'unico posto che li compone: e' lo stesso
     titolo del cartellino d'inizio partita e di quello pre-partita, che prima lo
     riscrivevano ciascuno per conto suo — e senza la forza. Qui la forma e'
     «APERTURA N° 56 (1N)»: il maiuscolo lo mette il CSS. */
  const titolo = titoloApertura(t, opening);
  const intestazione = titolo ? (
    <div className="move-list-opening" data-testid="move-list-opening">
      {titolo.titolo}
      {titolo.codice && <span className="move-list-opening-forza"> ({titolo.codice})</span>}
    </div>
  ) : null;

  /*
   * `!variant` e `moveHistory.length === 0` sono la stessa condizione: senza il
   * prop la variante e le mosse vengono dalla stessa `boardState`, quindi se non
   * c'e' la variante non c'e' nemmeno una mossa da scrivere. Sta nella guardia
   * e non in un default perche' un default qui sarebbe la notazione di un'altra
   * variante — numeri sbagliati che sembrano giusti.
   */
  if (moveHistory.length === 0 || !variant) {
    return (
      <div className={`move-list${blind ? ' move-list--blind' : ''}`} data-testid="move-list">
        {intestazione}
        <div className="move-list-empty">{t('game.sidebar.noMoves', 'No moves yet')}</div>
      </div>
    );
  }

  const pairs = pairMoves(moveHistory);
  // Currently-viewed ply: live => last move; reviewing N => move N-1 produced it.
  const activeMoveIndex = reviewIndex === null ? moveHistory.length - 1 : reviewIndex - 1;

  const handleClick = (plyIndex: number) => reviewToIndex(plyIndex + 1);
  const handleKeyDown = (e: React.KeyboardEvent, plyIndex: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(plyIndex);
    }
  };

  // ALLA CIECA LA LISTA E' UNA LETTURA, NON UNA NAVIGAZIONE.
  // Senza il «vai alla posizione attuale» — che sparisce con le frecce — un tocco su una
  // mossa passata lascerebbe la damiera bloccata in revisione senza via d'uscita
  // visibile: l'unica rimasta sarebbe toccare proprio l'ULTIMA mossa, che lo store
  // rimappa su «live», e non lo indovina nessuno. E non ci sarebbe niente da vedere:
  // sotto la benda anche la posizione rivista e' vuota.
  //
  // Un solo oggetto sparso su tutte e due le celle (e identico nei due file) perche' due
  // copie dello stesso ramo sono due occasioni di divergere.
  // Le semimosse dell'apertura sorteggiata non le ha giocate nessuno dei due:
  // in grigio e corsivo, cosi' si vede dove comincia la partita vera.
  const semimosseApertura = opening?.moves?.length ?? 0;
  const classeCella = (plyIndex: number) =>
    `move-list-move${plyIndex === activeMoveIndex ? ' current-move' : ''}${plyIndex < semimosseApertura ? ' move-list-move--apertura' : ''}`;

  const cellProps = (plyIndex: number) =>
    blind
      ? {}
      : {
          role: 'button' as const,
          tabIndex: 0,
          'aria-label': t('game.sidebar.viewMove', 'View position after move {{n}}', { n: plyIndex + 1 }),
          onClick: () => handleClick(plyIndex),
          onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, plyIndex),
        };

  return (
    <div className={`move-list${blind ? ' move-list--blind' : ''}`} data-testid="move-list" ref={scrollRef}>
      {intestazione}
      <table className="move-list-table">
        <tbody>
          {pairs.map(([white, black], pairIndex) => {
            const whiteIndex = pairIndex * 2;
            const blackIndex = pairIndex * 2 + 1;
            return (
              <tr key={pairIndex} className="move-list-row">
                <td className="move-list-num">{pairIndex + 1}.</td>
                <td
                  className={classeCella(whiteIndex)}
                  data-testid={`move-white-${pairIndex}`}
                  {...cellProps(whiteIndex)}
                >
                  {formatMoveNotation(white, variant)}
                </td>
                {black ? (
                  <td
                    className={classeCella(blackIndex)}
                    data-testid={`move-black-${pairIndex}`}
                    {...cellProps(blackIndex)}
                  >
                    {formatMoveNotation(black, variant)}
                  </td>
                ) : (
                  <td className="move-list-move" data-testid={`move-black-${pairIndex}`} />
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default memo(MoveList);
